import axios, { AxiosError } from 'axios';
import { Auth } from 'aws-amplify';
import getUserSession from '@utils/getSession';
import { appVersion } from '../../constants/config';
import { Logger } from '../utils/logger';
import { SELECTED_COMPANY_ID } from '@constants/index';

export const api = axios.create({
  baseURL: import.meta.env.VITE_APP_ENDPOINT,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use(async (config) => {
  const location = window.location;
  const { token, selectedCompanyId } = await getUserSession();
  config.headers.Authorization = token;
  if (selectedCompanyId) {
    config.headers['x-company-id'] = selectedCompanyId;
  }
  config.headers['x-page'] =
    location.pathname + location.search + location.hash;
  if (appVersion) {
    config.headers['x-app-version'] = appVersion;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      try {
        Auth.currentAuthenticatedUser({ bypassCache: true });
      } catch (error) {
        localStorage.clear();
        window.location.href = '/login';
        Logger.info(
          'client::api::response: 401 error, clearing localStorage and redirecting to login'
        );
      }
    } else if (error.response && error.config?.url) {
      // not a network error
      Logger.error(
        `client::api::response: ${error.message} calling ${error.config.url}`,
        error
      );
    }
    if (
      error.response?.status === 403 &&
      error.response?.data.message === 'FORBIDDEN: Invalid Company'
    ) {
      const { selectedCompanyId } = await getUserSession();
      Logger.error(
        `client::api::response: 403 error, companyId ${selectedCompanyId} might be invalid. Clearing company ID in localStorage`
      );
      localStorage.removeItem(SELECTED_COMPANY_ID); // this reset the company selection
    }
    return Promise.reject(error);
  }
);
